import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import SEO from '../../components/SEO';
import SeoNav from '../../components/SeoNav';
import BlogCTA from '../../components/BlogCTA';
import RelatedArticles from '../../components/RelatedArticles';

const schema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Article',
      'headline': 'How to Number Your Invoices Professionally',
      'description': 'Sequential, date-based, and client-based numbering formats — plus the mistakes that create accounting headaches and how to avoid them.',
      'url': 'https://www.invilic.com/blog/how-to-number-invoices',
      'datePublished': '2025-05-20',
      'dateModified': '2025-05-20',
      'author': { '@type': 'Organization', 'name': 'Invilic', 'url': 'https://www.invilic.com' },
      'publisher': { '@type': 'Organization', 'name': 'Invilic', 'url': 'https://www.invilic.com' },
    },
    {
      '@type': 'FAQPage',
      'mainEntity': [
        {
          '@type': 'Question',
          'name': 'Do invoices need to be numbered sequentially?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'In most countries yes. The UK HMRC, Indian GST law, and EU VAT rules all require that VAT/GST invoices have a sequential invoice number with no gaps. For non-tax invoices (e.g., freelancers below the tax threshold), sequential numbering is not legally required but is strongly recommended for your own record-keeping and to maintain a professional appearance.' },
        },
        {
          '@type': 'Question',
          'name': 'Can I restart invoice numbering at the beginning of each year?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'Yes, you can reset your invoice number sequence at the start of each financial year — this is common practice and makes year-based searching easy. For example, your first invoice in 2026 becomes INV-2026-001. In India, GST law requires a fresh invoice number sequence for each financial year (April to March). Just ensure numbers remain unique across all invoices.' },
        },
        {
          '@type': 'Question',
          'name': 'What should I do if I accidentally skip an invoice number?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'Do not go back and reuse the skipped number or create a fake invoice to fill the gap — this could cause legal issues. Simply leave the gap and continue from the next number. If you are VAT or GST registered and an auditor notices, you can explain it was a data entry error or a voided invoice. Always void rather than delete invoices you no longer need.' },
        },
        {
          '@type': 'Question',
          'name': 'Is there a legal requirement for invoice numbers?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'For VAT-registered businesses in the UK, EU, and GST-registered businesses in India, yes — tax law mandates sequential unique invoice numbers. For non-registered businesses and freelancers below the tax threshold, the legal requirement is less strict, but the same principle applies for good record-keeping. Your bank, accountant, or auditor will expect numbering to make sense.' },
        },
      ],
    },
  ],
};

const faqs = schema['@graph'][1].mainEntity;

const formats = [
  {
    name: 'Simple Sequential',
    examples: ['INV-001', 'INV-002', 'INV-003'],
    pros: 'Simple, easy to remember, works from day one',
    cons: 'Does not give any date or client context',
    bestFor: 'Freelancers just starting out',
  },
  {
    name: 'Year-Sequential',
    examples: ['INV-2025-001', 'INV-2025-002', 'INV-2026-001'],
    pros: 'Instantly tells you which year, resets cleanly each year',
    cons: 'Slightly longer number',
    bestFor: 'Any business — most commonly used format',
  },
  {
    name: 'Date-Based',
    examples: ['INV-20250520-001', 'INV-20250521-001'],
    pros: 'Date embedded in the number — no need to look at invoice date',
    cons: 'Long and harder to remember',
    bestFor: 'Businesses with multiple invoices per day',
  },
  {
    name: 'Client-Based',
    examples: ['ACME-2025-001', 'SMITH-2025-001', 'SMITH-2025-002'],
    pros: 'Instantly identifies the client — useful for account management',
    cons: 'More complex to maintain across many clients',
    bestFor: 'Agencies and consultants with recurring clients',
  },
  {
    name: 'Project-Based',
    examples: ['PROJ-WEBSITEREB-001', 'PROJ-BRANDDSG-001'],
    pros: 'Links invoice directly to a project — useful for project billing',
    cons: 'Long abbreviations can be confusing',
    bestFor: 'Project-based agencies',
  },
];

export default function InvoiceNumberingPage() {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <SEO
        title="How to Number Your Invoices Professionally | Invilic Blog"
        description="Invoice numbering formats explained — sequential, date-based, client-based. Which to use, how to reset yearly, and common mistakes to avoid."
        canonical="https://www.invilic.com/blog/how-to-number-invoices"
        ogType="article"
        schema={schema}
      />
      <SeoNav />

      <main className="max-w-3xl mx-auto px-6 py-16">
        <header className="mb-10">
          <div className="flex items-center gap-2 mb-5 text-sm">
            <Link to="/blog" className="text-blue-600 dark:text-blue-400 hover:underline">Blog</Link>
            <ArrowRight className="w-3 h-3 text-gray-400" />
            <span className="text-gray-500 dark:text-gray-400">Basics</span>
          </div>
          <span className="inline-block bg-slate-100 dark:bg-slate-800/50 text-slate-700 dark:text-slate-300 text-xs font-semibold px-2.5 py-1 rounded-full mb-4">Basics</span>
          <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-900 dark:text-white leading-tight mb-4">
            How to Number Your Invoices Professionally
          </h1>
          <p className="text-gray-500 dark:text-gray-400 text-sm">May 20, 2025 · 4 min read</p>
        </header>

        <article className="text-gray-700 dark:text-gray-300 leading-relaxed">

          <p className="text-lg mb-6">
            Every invoice needs a unique number — not just for professionalism, but because tax law in most countries (UK VAT, India GST, EU VAT) requires it. The most practical format is year-sequential: INV-2025-001, INV-2025-002. It is readable, sortable, and resets cleanly each year.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-4">Why Invoice Numbers Matter</h2>
          <p className="mb-4">
            An invoice number is the primary reference both you and your client use when discussing a payment. When your client's accounts payable team processes your invoice, they log it by number. When you chase a late payment, you reference the number. When a tax authority audits you, they trace transactions by invoice number.
          </p>
          <p className="mb-6">
            Gaps in invoice numbering — or duplicate numbers — are red flags in an audit. They suggest missing transactions or errors in record-keeping, even if the real reason was simply a typo. A consistent numbering system eliminates this risk entirely.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-4">5 Invoice Numbering Formats Compared</h2>
          <div className="space-y-4 mb-8">
            {formats.map(({ name, examples, pros, cons, bestFor }) => (
              <div key={name} className="border border-gray-100 dark:border-gray-800 rounded-2xl p-5">
                <p className="font-bold text-gray-900 dark:text-white mb-3">{name}</p>
                <div className="flex flex-wrap gap-2 mb-3">
                  {examples.map(ex => (
                    <span key={ex} className="font-mono text-xs bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 px-2.5 py-1 rounded-lg">{ex}</span>
                  ))}
                </div>
                <div className="grid sm:grid-cols-3 gap-3 text-xs">
                  <div><span className="font-semibold text-green-700 dark:text-green-400">Pros: </span><span className="text-gray-500 dark:text-gray-400">{pros}</span></div>
                  <div><span className="font-semibold text-red-600 dark:text-red-400">Cons: </span><span className="text-gray-500 dark:text-gray-400">{cons}</span></div>
                  <div><span className="font-semibold text-blue-700 dark:text-blue-400">Best for: </span><span className="text-gray-500 dark:text-gray-400">{bestFor}</span></div>
                </div>
              </div>
            ))}
          </div>

          <BlogCTA
            text="Invilic auto-generates sequential invoice numbers (INV-000001 format) for every invoice. Change the prefix to match your style."
            to="/create/freelancer"
            btnText="Create Invoice Free"
          />

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-4">Invoice Numbering Rules by Country</h2>
          <div className="space-y-3 mb-8">
            {[
              { country: 'India (GST)', rule: 'Must be a consecutive serial number unique per financial year (April–March). Up to 16 characters. Can include letters, numbers, / and - only. Reset every April 1.' },
              { country: 'United Kingdom (VAT)', rule: 'Must be a unique sequential number. HMRC does not specify format but requires no gaps in the sequence. Many businesses use a prefix + year + number format.' },
              { country: 'European Union (VAT)', rule: 'Must be sequential. EU VAT Directive requires unique, sequential numbering. The sequence may use numbers alone or include letters/dates. Reset per year is permitted.' },
              { country: 'United States', rule: 'No federal requirement on invoice numbering for non-tax invoices. However, sequential numbering is standard practice for accounting and audit purposes.' },
            ].map(({ country, rule }) => (
              <div key={country} className="p-4 border border-gray-100 dark:border-gray-800 rounded-xl">
                <p className="font-semibold text-gray-900 dark:text-white text-sm mb-1">{country}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">{rule}</p>
              </div>
            ))}
          </div>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-4">Common Mistakes to Avoid</h2>
          <ul className="space-y-3 mb-8">
            {[
              'Starting every invoice at 001 regardless of year — makes it impossible to tell which year an invoice belongs to without checking the date.',
              'Using invoice dates as numbers — e.g., 20052025 — this looks like a valid number but creates collisions if you issue multiple invoices on the same date.',
              'Deleting invoices instead of voiding them — always void cancelled invoices and keep them in your records. Deleting creates gaps.',
              'Inconsistent prefixes across different invoice types — pick one system and stick with it. Mixing INV-, INVOICE-, and no prefix creates confusion.',
              'Reusing numbers from previous years — even if you reset your sequence, make the year part of the number so old and new INV-001 are distinguishable.',
            ].map(mistake => (
              <li key={mistake} className="flex items-start gap-2 text-sm">
                <span className="text-red-500 font-bold flex-shrink-0">✗</span>
                {mistake}
              </li>
            ))}
          </ul>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-6">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {faqs.map(({ name, acceptedAnswer }) => (
              <details key={name} className="group border border-gray-100 dark:border-gray-800 rounded-xl overflow-hidden">
                <summary className="flex items-center justify-between px-5 py-4 cursor-pointer font-medium text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-900/50 list-none text-sm">
                  {name}
                  <ArrowRight className="w-4 h-4 text-gray-400 transition-transform group-open:rotate-90 flex-shrink-0 ml-4" />
                </summary>
                <p className="px-5 pb-4 text-sm text-gray-500 dark:text-gray-400 leading-relaxed">{acceptedAnswer.text}</p>
              </details>
            ))}
          </div>

          <BlogCTA
            text="Invilic handles invoice numbering automatically — sequential, customisable prefix, resets per year. Free, no account needed."
            to="/free-invoice-generator"
            btnText="Try Invilic Free"
          />

        </article>
        <RelatedArticles current="/blog/how-to-number-invoices" />
      </main>
    </div>
  );
}
